import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { TaskSearchForm } from '../services/dtos/taskSearchForm';
import { Task } from './dtos/task';
import {TaskContent} from './dtos/taskContent'
import { TasksProcessingServiceState } from './dtos/tasksProcessingServiceState';

import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { TaskInfoResponse} from './dtos/taskInfoResponse';
import { ProcessType } from './enums/processType.enum';

@Injectable({ providedIn: 'root' })
export class TasksService {

  private tasksUrl = this.baseUrl + '/api/tasks';

  public constructor(
    private http: HttpClient,
    private toastr: ToastrService,
    @Inject('API_BASE_URL') private baseUrl: string) { }

  public getAllBasicInfo(from: string, to: string, filters: TaskSearchForm): Observable<TaskInfoResponse> {
    let params = new HttpParams();
    params = params.append('from', from);
    params = params.append('to', to);
    if (filters.searchTerm) { params = params.append('searchTerm', filters.searchTerm); }
    if (filters.customerCode) { params = params.append('customerCode', filters.customerCode); }
    if (filters.customerLaboratory) { params = params.append('customerLaboratory', filters.customerLaboratory); }
    if (filters.messageStates && filters.messageStates.length > 0) {
      filters.messageStates.forEach(state => {
        params = params.append('messageStates', state);
      });
    }
    if (filters.processFlows) { params = params.append('processFlows', filters.processFlows); }
    if (filters.processTypes && filters.processTypes.length > 0) {
      filters.processTypes.forEach(type => {
        params = params.append('processTypes', type);
      });
    }
    if (filters.customer) { params = params.append('customer', filters.customer); }
    if (filters.customers) { params = params.append('customers', filters.customers); }

    return this.http.get<TaskInfoResponse>(this.tasksUrl + '/GetAllBasicInfo', { params })
      .pipe(
        catchError(this.handleError<TaskInfoResponse>('getAllBasicInfo'))
      );
  }

  public getTask(id: string): Observable<Task> {
    return this.http.get<Task>(this.tasksUrl + '/Get/' + id)
      .pipe(
        catchError(this.handleError<Task>('getTask'))
      );
  }

  public getTaskContent(id: string, processType: ProcessType): Observable<TaskContent> {
    let params = new HttpParams();

    params = params.append('processType', processType);
    return this.http.get<TaskContent>(this.tasksUrl + '/GetTaskContent/' + id, {params})
      .pipe(
        catchError(this.handleError<TaskContent>('getTaskContent'))
      );
  }

  public getTasksProcessingServiceState(): Observable<TasksProcessingServiceState> {
    return this.http.get<TasksProcessingServiceState>(this.tasksUrl + '/GetTasksProcessingServiceState')
      .pipe(
        catchError(this.handleError<TasksProcessingServiceState>('getTasksProcessingServiceState'))
      );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public deleteTask(taskId: string): Observable<any> {
    return this.http.delete(this.tasksUrl + '/Delete/' + taskId)
      .pipe(
        tap(() => this.toastr.success('Deleted Sucessfully')),
        catchError(this.handleError<Task>('deleteTask'))
      );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public startTasksProcessingService(): Observable<any> {
    return this.http.post(this.tasksUrl + '/StartTasksProcessingService', null)
      .pipe(
        tap(() => this.toastr.success('Tasks Processing Sucessfully Started')),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        catchError(this.handleError<any>('startTasksProcessingService'))
      );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public pauseTasksProcessingService(): Observable<any> {
    return this.http.post(this.tasksUrl + '/PauseTasksProcessingService', null)
      .pipe(
        tap(() => this.toastr.success('Tasks Processing Sucessfully Paused')),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        catchError(this.handleError<any>('pauseTasksProcessingService'))
      );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public scheduleTaskForReRun(taskToUpdate: Task): Observable<any> {
    return this.http.put(this.tasksUrl + '/ScheduleTaskForReRun/' + taskToUpdate.id, taskToUpdate)
      .pipe(
          tap(() => this.toastr.success('Task was scheduled for re-run')),
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          catchError(this.handleError<any>('scheduleTaskForReRun'))
      );
  }

  // eslint-disable-next-line  @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars
  private handleError<T>(operation = 'operation', result?: T | undefined): (err: any) => Observable<T> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (error: any): Observable<T> => {
      console.error('An error occurred', error);
      this.toastr.error(`${operation} failed: ${error.message}`);

      throw error;
    };
  }
}
